import React, { useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import SortableContainer from './SortableContainer';
import { Item } from '../../atoms/DraggableComponent/SortableItem';
import { StyledWrapper } from './DraggableComponent.styles';
import { CiCirclePlus, CiSearch } from 'react-icons/ci';
import EditableLabel from './EditableLabel';
import { GoPlus } from 'react-icons/go';



export default function DragableComponent({
  initialItems = {
    root: [
      { id: '1', title: 'First Name', text: 'John' },
      { id: '2', title: 'Last Name', text: 'Doe' },
    ],
    container1: [{ id: '3', title: 'Company', text: 'Acme Inc.' }],
  },
  onItemsChange,
  columnName,
  dragableData,
  onEditLabel = () => { },
  taskMapping = false,
  onAddField = () => { },
}) {
  const [items, setItems] = useState(initialItems);
  const [activeId, setActiveId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    // Expose full item objects (or nulls) to the parent so it can
    // access rowKey / title metadata for mapping.
    onItemsChange?.(items);
  }, [items, onItemsChange]);

  const filteredItems = items.container1?.filter(item =>
    item?.title?.toLowerCase().includes(searchQuery?.toLowerCase()),
  );

  const findContainer = id => {
    if (items.root?.some(item => item?.id === id)) return 'root';
    if (items.container1?.some(item => item?.id === id)) return 'container1';
    return null;
  };

  const findItemById = id => {
    if (!id) return null;
    return (
      items.root?.find(item => item?.id === id) ||
      items.container1?.find(item => item?.id === id) ||
      null
    );
  };

  const handleDragStart = ({ active }) => {
    const container = findContainer(active?.id);
    if (!container) return;

    setActiveId(active?.id); // unique item ID
  };

  const handleDragOver = ({ active, over }) => {
    // Only support reordering within the same container.
    // Moving items between containers is handled exclusively in handleDragEnd
    // to avoid race conditions that can create visual duplicates when dragging fast.
    const activeId = active.id;
    const overId = over?.id;

    const activeContainer = findContainer(activeId);
    const overContainer = overId ? findContainer(overId) : null;

    if (!activeContainer || !overContainer || activeContainer !== overContainer || activeId === overId) return;

    setItems(prev => {
      const containerItems = [...(prev[activeContainer] || [])];
      const oldIndex = containerItems.findIndex(item => item?.id === activeId);
      const newIndex = containerItems.findIndex(item => item?.id === overId);

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;

      const updated = [...containerItems];
      const [moved] = updated.splice(oldIndex, 1);
      updated.splice(newIndex, 0, moved);

      return {
        ...prev,
        [activeContainer]: updated,
      };
    });
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveId(null);

    const sourceContainer = findContainer(active.id);
    if (!sourceContainer || !items[sourceContainer]) return;

    const draggedItem = items[sourceContainer].find(item => item?.id === active.id);
    if (!draggedItem) return;

    // Helper: insert into CSV list near original index
    const insertIntoCsvAtOriginalIndex = (list, item) => {
      const target = [...(list || [])];
      if (target.some(x => x?.id === item.id)) return target;

      const originalIndex =
        typeof item.originalIndex === 'number' && item.originalIndex >= 0
          ? item.originalIndex
          : target.length;

      const insertIndex = Math.min(originalIndex, target.length);
      target.splice(insertIndex, 0, item);
      return target;
    };

    // If dropped outside any target: if it came from root, unmap back to CSV
    if (!over) {
      if (sourceContainer === 'root') {
        setItems(prev => {
          const newItems = { ...prev };
          newItems.root = (newItems.root || []).map(i => (i?.id === active.id ? null : i));
          newItems.container1 = insertIntoCsvAtOriginalIndex(newItems.container1, draggedItem);
          return newItems;
        });
      }
      return;
    }

    // Determine drop container and slot index.
    // `over.id` can be either a slot id (e.g. "root-slot-1")
    // or an item id (e.g. the id of a mapped field). We support both.
    let containerId;
    let slotIndex = -1;

    const overContainerByItemId = findContainer(over.id);
    if (overContainerByItemId === 'root') {
      containerId = 'root';
      slotIndex = (items.root || []).findIndex(i => i?.id === over.id);
    } else if (overContainerByItemId === 'container1') {
      containerId = 'container1';
    } else {
      const overIdParts = String(over.id).split('-');
      containerId = overIdParts[0];
      slotIndex = Number(overIdParts[2]);
    }

    setItems(prev => {
      const newItems = { ...prev };

      // Moving into mapped "root" slots
      if (containerId === 'root') {
        if (Number.isNaN(slotIndex)) return prev;

        const rootArray = Array.from({ length: dragableData.length }, (_, i) => newItems.root?.[i] || null);

        if (sourceContainer === 'root') {
          // Reordering within mapped area: move from oldIndex to slotIndex
          const oldIndex = rootArray.findIndex(i => i?.id === active.id);
          if (oldIndex === -1 || oldIndex === slotIndex) return prev;
          const updated = [...rootArray];
          const [moved] = updated.splice(oldIndex, 1);
          updated.splice(slotIndex, 0, moved);
          newItems.root = updated;
          return newItems;
        }

        // Coming from CSV list -> map into slot
        // Remove from CSV
        newItems.container1 = (newItems.container1 || []).filter(i => i?.id !== active.id);

        // If there is already an item in this mapped slot, shift everything
        // down by one from this index until we find an empty slot.
        let carry = draggedItem;
        for (let i = slotIndex; i < rootArray.length; i++) {
          const current = rootArray[i];
          rootArray[i] = carry;
          carry = current;
          if (!carry) break;
        }
        newItems.root = rootArray;
        return newItems;
      }

      // Drops onto CSV Data area or anywhere else:
      // - If already in CSV list, let handleDragOver's reorder stand (no-op).
      // - If coming from root, unmap back to CSV while preserving original order.
      if (sourceContainer === 'container1') {
        return prev;
      }

      newItems.root = (newItems.root || []).map(i => (i?.id === active.id ? null : i));
      newItems.container1 = insertIntoCsvAtOriginalIndex(newItems.container1, draggedItem);
      return newItems;
    });
  }

  return (
    <StyledWrapper>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}>
        {Object.entries(items).map(([containerId, containerItems]) =>
          containerId === 'root' ? (
            <div key={containerId} className="fields-wrapper-holder">
              <div className="field-wrapper">
                <div className="col-holder">
                  <div className="col first-col">
                    <span className="heading">Houdiny Fields</span>
                  </div>

                  {dragableData?.map((item, index) => (
                    <div className="col" key={index}>
                      <EditableLabel
                        value={item.label}
                        editable={item.editable}
                        reuired={item.required}
                        onChange={newVal => onEditLabel?.(index, newVal)}
                      />
                    </div>
                  ))}

                  {/* + Add New Column button */}
                  <div className="col add-new-col" onClick={onAddField}>
                    <div className="icon-holder">
                      <GoPlus size={20} />
                    </div>
                    Add new column
                  </div>
                </div>

                <div className="col-holder csv-col">
                  <div className="col first-col">
                    <span className="heading">Mapped Data</span>
                  </div>
                  <SortableContainer
                    id={containerId}
                    items={containerItems}
                    emptySlots
                    slotCount={dragableData?.length}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div key={containerId} className="col-holder search">
              <div className="col search-col">
                <span className="subtitle">CSV Data</span>
                <div className="search-holder">
                  <CiSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search Column"
                    value={searchQuery} // Bind input value to state
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="columns">
                <SortableContainer id={containerId} items={filteredItems} />
              </div>
            </div>
          ),
        )}

        {(() => {
          const activeItem = findItemById(activeId);
          return (
            <DragOverlay>
              {activeItem ? <Item title={activeItem.title} text="" id={activeId} /> : null}
            </DragOverlay>
          );
        })()}
      </DndContext>
    </StyledWrapper>
  );
}
